import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { APIuuid, IAPIData } from '@shared/interfaces/api.interface';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface IScheduledCandidate {
  id: APIuuid;
  first_name: string;
  last_name: string;
  email: string;
  last_initiated_at: string;
  scheduled_for_anonymization_at: string;
}

export interface IScheduledFeedback {
  id: APIuuid;
  identifiable_type: string | null;
  identifiable_id: APIuuid | null;
  last_initiated_at: string;
  scheduled_for_anonymization_at: string;
}

export interface IScheduledEntitiesResult {
  candidates: IScheduledCandidate[];
  feedbacks: IScheduledFeedback[];
  retentionPeriodMonths: number;
}

export interface IBulkExcludeResult {
  succeeded: APIuuid[];
  failed: APIuuid[];
}

@Injectable({
  providedIn: 'root'
})
export class DataRetentionService {
  private readonly ENDPOINT_V1_CANDIDATES: string = String(
    `${environment.ENDPOINTS.BACK_END.V1}/candidates`
  );

  constructor(private readonly _http: HttpClient) {}

  /**
   * Records already eligible or becoming eligible within the next `days` days.
   * The server computes scheduled_for_anonymization_at per record and returns
   * the firm's effective retention period in meta.
   */
  public getScheduledEntities(
    days: number = 30
  ): Observable<IScheduledEntitiesResult> {
    return this._http
      .get<any>(
        `${this.ENDPOINT_V1_CANDIDATES}/scheduled_for_anonymization?days=${days}`
      )
      .pipe(
        map((res: any) => ({
          candidates: (res.data?.candidates || []).map(
            (c: any) => c.attributes as IScheduledCandidate
          ),
          feedbacks: (res.data?.feedbacks || []).map(
            (f: any) => f.attributes as IScheduledFeedback
          ),
          retentionPeriodMonths: res.meta?.retention_period_months
        }))
      );
  }

  /**
   * Durably exclude a candidate from retention anonymization. Returns the raw
   * API payload — callers only care about success/failure.
   */
  public excludeCandidateFromAnonymization(id: APIuuid): Observable<IAPIData> {
    return this._http.post<IAPIData>(
      `${this.ENDPOINT_V1_CANDIDATES}/${id}/exclude_from_anonymization`,
      {}
    );
  }

  /**
   * Exclude multiple candidates. Individual failures do not abort the batch;
   * the result reports which ids succeeded and which failed.
   */
  public excludeCandidatesFromAnonymization(
    ids: APIuuid[]
  ): Observable<IBulkExcludeResult> {
    if (ids.length === 0) {
      return of({ succeeded: [], failed: [] });
    }

    const requests = ids.map((id) =>
      this.excludeCandidateFromAnonymization(id).pipe(
        map(() => ({ id, ok: true })),
        catchError(() => of({ id, ok: false }))
      )
    );

    return forkJoin(requests).pipe(
      map((results) => ({
        succeeded: results.filter((r) => r.ok).map((r) => r.id),
        failed: results.filter((r) => !r.ok).map((r) => r.id)
      }))
    );
  }
}
